import React, { useState, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

import { AuthProvider, UserRole } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './layouts/MainLayout';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import TouchstreamConfig from './pages/Admin/TouchstreamConfig';
import S3Buckets from './pages/Admin/S3Buckets';
import E2EScenarios from './pages/Admin/E2EScenarios';
import UserManagement from './pages/Admin/UserManagement';
import Login from './pages/Login';
import Player from './pages/Player';

const ThemeContext = createContext();

export const useThemeMode = () => useContext(ThemeContext);

const createCustomTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#00d4ff' : '#1976d2',
      dark: mode === 'dark' ? '#0099cc' : '#115293',
      light: mode === 'dark' ? '#4ddfff' : '#42a5f5',
    },
    secondary: {
      main: mode === 'dark' ? '#ff0080' : '#dc004e',
      dark: mode === 'dark' ? '#cc0066' : '#9a0036',
      light: mode === 'dark' ? '#ff4da6' : '#e91e63',
    },
    background: {
      default: mode === 'dark' ? '#0a0a0a' : '#f5f5f5',
      paper: mode === 'dark' ? 'rgba(26, 26, 26, 0.8)' : '#ffffff',
    },
    text: {
      primary: mode === 'dark' ? '#ffffff' : '#1a1a1a',
      secondary: mode === 'dark' ? '#b3b3b3' : '#666666',
    },
    divider: mode === 'dark' ? 'rgba(0, 212, 255, 0.1)' : '#e0e0e0',
    success: {
      main: mode === 'dark' ? '#00ff88' : '#4caf50',
    },
    warning: {
      main: mode === 'dark' ? '#ffaa00' : '#ff9800',
    },
    error: {
      main: mode === 'dark' ? '#ff4444' : '#f44336',
    },
    info: {
      main: mode === 'dark' ? '#66b3ff' : '#2196f3',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '3rem',
      letterSpacing: '-0.01562em',
    },
    h4: {
      fontWeight: 600,
      fontSize: '2rem',
      letterSpacing: '0.00735em',
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.25rem',
      letterSpacing: '0.0075em',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: mode === 'dark'
            ? 'linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 50%, #16213e 100%)'
            : 'linear-gradient(135deg, #f5f5f5 0%, #e8eaf6 50%, #e3f2fd 100%)',
          minHeight: '100vh',
          backgroundAttachment: 'fixed',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: mode === 'dark'
            ? 'linear-gradient(90deg, rgba(26, 26, 46, 0.95) 0%, rgba(22, 33, 62, 0.95) 100%)'
            : 'linear-gradient(90deg, #1976d2 0%, #1565c0 100%)',
          backdropFilter: 'blur(10px)',
          borderBottom: mode === 'dark' ? '1px solid rgba(0, 212, 255, 0.2)' : 'none',
          boxShadow: mode === 'dark'
            ? '0 4px 30px rgba(0, 212, 255, 0.1)'
            : '0 2px 4px rgba(0,0,0,0.1)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          background: mode === 'dark'
            ? 'rgba(26, 26, 26, 0.8)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: mode === 'dark' ? '1px solid rgba(0, 212, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
          boxShadow: mode === 'dark'
            ? '0 8px 32px rgba(0, 212, 255, 0.1)'
            : '0 2px 8px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          background: mode === 'dark'
            ? 'rgba(26, 26, 26, 0.8)'
            : 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(10px)',
          border: mode === 'dark' ? '1px solid rgba(0, 212, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: mode === 'dark'
              ? '0 12px 40px rgba(0, 212, 255, 0.15)'
              : '0 4px 20px rgba(0, 0, 0, 0.15)',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        contained: {
          background: mode === 'dark'
            ? 'linear-gradient(45deg, #00d4ff 30%, #0099cc 90%)'
            : 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
          border: 0,
          borderRadius: 8,
          boxShadow: mode === 'dark'
            ? '0 3px 15px rgba(0, 212, 255, 0.3)'
            : '0 3px 5px 2px rgba(25, 118, 210, 0.3)',
          color: 'white',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.3s ease-in-out',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: mode === 'dark'
              ? '0 6px 20px rgba(0, 212, 255, 0.4)'
              : '0 6px 10px 2px rgba(25, 118, 210, 0.4)',
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 8,
          borderRadius: 4,
          background: mode === 'dark'
            ? 'rgba(255, 255, 255, 0.1)'
            : 'rgba(0, 0, 0, 0.1)',
        },
        bar: {
          borderRadius: 4,
          background: mode === 'dark'
            ? 'linear-gradient(90deg, #00d4ff 0%, #00ff88 100%)'
            : 'linear-gradient(90deg, #1976d2 0%, #4caf50 100%)',
          boxShadow: mode === 'dark'
            ? '0 0 10px rgba(0, 212, 255, 0.5)'
            : 'none',
        },
      },
    },
  },
});

function App() {
  const [themeMode, setThemeMode] = useState('dark');

  const toggleTheme = () => {
    setThemeMode(prevMode => prevMode === 'light' ? 'dark' : 'light');
  };

  const theme = createCustomTheme(themeMode);

  return (
    <ThemeContext.Provider value={{ themeMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <AuthProvider>
            <Routes>
              {/* Public route */}
              <Route path="/login" element={<Login />} />

              {/* Protected routes wrapped in MainLayout */}
              <Route
                path="/"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.DASHBOARD, UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <Navigate to="/dashboard" replace />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.DASHBOARD, UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <Dashboard />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <Admin />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/touchstream"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <TouchstreamConfig />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/s3"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <S3Buckets />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/e2e-scenarios"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <E2EScenarios />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <UserManagement />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/player/:pipelineId"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.DASHBOARD, UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <Player />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/player"
                element={
                  <ProtectedRoute allowedRoles={[UserRole.DASHBOARD, UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN]}>
                    <MainLayout>
                      <Player />
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </AuthProvider>
        </Router>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;